import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@homy/ui";
import { isNavLeafActive } from "@/components/layout/appNavConfig";
import { useHoverOpenNavDropdown } from "@/components/layout/useHoverOpenNavDropdown";
import { NavLeafButton } from "./NavLeafButton";
import { CollapsedNavGroupDropdown } from "./CollapsedNavGroupDropdown";

/**
 * @param {{
 *   groups: import("@/components/layout/appNavConfig").AppNavGroup[];
 *   collapsed: boolean;
 *   groupPresentation?: 'desktopExpanded' | 'inline' | null;
 *   openGroups: Set<string>;
 *   onToggleGroup: (id: string) => void;
 *   pathname: string;
 *   onNavigate: (path: string) => void;
 * }} props
 */
export function AppNavSections({
  groups,
  collapsed,
  groupPresentation = null,
  openGroups,
  onToggleGroup,
  pathname,
  onNavigate,
}) {
  const { t } = useTranslation("common");
  const hoverOpen = useHoverOpenNavDropdown();

  return (
    <>
      {groups.map((section, idx) => {
        if (section.type === "single") {
          return (
            <div
              key={section.items.map((i) => i.id).join("-")}
              className="space-y-1"
            >
              {section.items.map((leaf) => (
                <NavLeafButton
                  key={leaf.id}
                  leaf={leaf}
                  collapsed={collapsed}
                  isActive={isNavLeafActive(leaf, pathname)}
                  onNavigate={onNavigate}
                  density={
                    collapsed || groupPresentation === "desktopExpanded"
                      ? "compactDesktop"
                      : "default"
                  }
                />
              ))}
            </div>
          );
        }

        const isOpen = openGroups.has(section.id);
        const groupHasActiveChild = section.children.some((l) =>
          isNavLeafActive(l, pathname),
        );

        if (collapsed) {
          return (
            <CollapsedNavGroupDropdown
              key={section.id}
              section={section}
              sectionIndex={idx}
              pathname={pathname}
              onNavigate={onNavigate}
              hoverOpen={hoverOpen}
            />
          );
        }

        const syncCollapsible = (next) => {
          if (next !== isOpen) onToggleGroup(section.id);
        };

        if (groupPresentation === "desktopExpanded") {
          return (
            <div key={section.id} className={cn(idx > 0 && "mt-0.5")}>
              <Collapsible open={isOpen} onOpenChange={syncCollapsible}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    id={`nav-group-trigger-${section.id}`}
                    aria-controls={`nav-group-panel-${section.id}`}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-lg px-2 py-2 min-h-[44px] text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar md:min-h-8 md:py-1.5",
                      groupHasActiveChild
                        ? "text-foreground"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                    )}
                    aria-expanded={isOpen}
                    aria-label={t("nav.toggleGroup", { group: section.label })}
                  >
                    <ChevronRight
                      className={cn(
                        "size-4 shrink-0 transition-transform duration-200",
                        isOpen && "rotate-90",
                      )}
                      aria-hidden
                    />
                    <span className="flex-1 text-xs font-semibold uppercase tracking-wide">
                      {section.label}
                    </span>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <ul
                    id={`nav-group-panel-${section.id}`}
                    className="mx-3.5 flex min-w-0 translate-x-px flex-col gap-1.5 border-l border-border/60 px-2.5 py-1"
                  >
                    {section.children.map((leaf) => (
                      <li key={leaf.id}>
                        <NavLeafButton
                          leaf={leaf}
                          collapsed={false}
                          isActive={isNavLeafActive(leaf, pathname)}
                          onNavigate={onNavigate}
                          density="subDesktop"
                        />
                      </li>
                    ))}
                  </ul>
                </CollapsibleContent>
              </Collapsible>
            </div>
          );
        }

        return (
          <div key={section.id} className={cn(idx > 0 && "mt-1")}>
            <Collapsible open={isOpen} onOpenChange={syncCollapsible}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  id={`nav-group-trigger-${section.id}`}
                  aria-controls={`nav-group-panel-${section.id}`}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-lg px-2 py-2 min-h-[44px] text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
                    groupHasActiveChild
                      ? "text-foreground"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                  )}
                  aria-expanded={isOpen}
                  aria-label={t("nav.toggleGroup", { group: section.label })}
                >
                  <ChevronDown
                    className={cn(
                      "size-4 shrink-0 transition-transform",
                      !isOpen && "-rotate-90",
                    )}
                    aria-hidden
                  />
                  <span className="flex-1 text-xs font-semibold uppercase tracking-wide">
                    {section.label}
                  </span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div
                  id={`nav-group-panel-${section.id}`}
                  role="region"
                  aria-labelledby={`nav-group-trigger-${section.id}`}
                  className="ml-1 space-y-1 border-l border-border/60 pl-2 pb-1"
                >
                  {section.children.map((leaf) => (
                    <NavLeafButton
                      key={leaf.id}
                      leaf={leaf}
                      collapsed={false}
                      isActive={isNavLeafActive(leaf, pathname)}
                      onNavigate={onNavigate}
                      density="default"
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        );
      })}
    </>
  );
}
